import { AssignmentDTOIsAdministratorDTO, AssignmentDTOIsClientDTO, AssignmentDTOIsCreativeDTO } from 'utils/typeguards'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { AssignmentStage } from 'constants/assignment'
import constate from 'constate'
import { uniqueId } from 'lodash'
import { useAuth0 } from 'utils/auth'
import { toVirtualVisitUrl } from 'utils/product/virtualVisitUrl'
import { useGalleryAssignment, useGalleryProduct } from '../../_main/contexts'
import { useSaveVirtualVisitLinks } from 'dataQueries'

/** Represents the instruction steps. */
export enum InstructionSteps {
  STEP_ID_DESCRIPTION = 'STEP_ID_DESCRIPTION',
  STEP_MATTERPORT_ACCOUNT = 'STEP_MATTERPORT_ACCOUNT',
  STEP_TOUR_NAME_MATCHES_CLIENT = 'STEP_TOUR_NAME_MATCHES_CLIENT',
  STEP_COPIED_LINK = 'STEP_COPIED_LINK'
}

/** Translation key of each instruction step's label. */
export const instructionStepLabelKeys: Readonly<Record<InstructionSteps, string>> = {
  [InstructionSteps.STEP_ID_DESCRIPTION]: 'upload_virtual_visit_instructions.instruction_1',
  [InstructionSteps.STEP_MATTERPORT_ACCOUNT]: 'upload_virtual_visit_instructions.instruction_2',
  [InstructionSteps.STEP_TOUR_NAME_MATCHES_CLIENT]: 'upload_virtual_visit_instructions.instruction_floorfy_tour_name',
  [InstructionSteps.STEP_COPIED_LINK]: 'upload_virtual_visit_instructions.instruction_3',
}

/** Represents the upload status options. */
export enum UploadStatus {
  ADMIN_LINKS_UPLOADED = 'ADMIN_LINKS_UPLOADED',
  CT_LINKS_UPLOADED = 'CT_LINKS_UPLOADED',
  CT_TO_SUBMIT_LINKS = 'CT_TO_SUBMIT_LINKS',
  ADMIN_WAITING_UPLOAD = 'ADMIN_WAITING_UPLOAD'
}

/** Represents a link item object. */
type LinkItem = {
  [key: string]: {
    link: string
    error?: boolean
  }
}

/**
 * Generates a unique ID for a link input.
 *
 * @returns The generated unique ID.
 */
const _generateLinkId = () => uniqueId('link-')

/**
 * Generates a default link item.
 *
 * @returns The generated default link item object.
 */
const _generateDefaultLink = (): LinkItem => ({ [`link-${_generateLinkId()}`]: { link: '' } })

/** A set of stages associated with displaying delivered links. */
const stagesDisplayingDeliveredLinks = new Set([
  AssignmentStage.VISUALS_SENT_BY_CT_TO_BKBN,
  AssignmentStage.VISUALS_SENT_BY_EDITOR_TO_BKBN,
  AssignmentStage.VISUALS_SENT_TO_CLIENT
])

/** Regular expressions to match matterport links in different formats. */
const matterportPatterns = [
  /.*my\.matterport\.com\/show\/\?m=(\w+)&.+/im,
  /.*my\.matterport\.com\/show\/\?m=(\w+)'.+/im,
  /.*my\.matterport\.com\/show\/\?m=(\w+)".+/im,
  /.*my\.matterport\.com\/show\/\?m=(\w+)/im,
]

export const [GalleryVirtualVisitInstructionsContextProvider, useGalleryVirtualVisitInstructions] = constate(() => {

  const { roles } = useAuth0()
  const { assignmentData } = useGalleryAssignment()
  const { isVirtualVisitFloorfy } = useGalleryProduct()

  /**
   * The steps the creative must tick. A Floorfy tour lives in the client's Floorfy account under the name the
   * client gave in the order comments, so it is checked by that name and never goes through Backbone's Matterport account.
   */
  const instructionSteps = useMemo(() => (
    isVirtualVisitFloorfy
      ? [InstructionSteps.STEP_TOUR_NAME_MATCHES_CLIENT, InstructionSteps.STEP_COPIED_LINK]
      : [InstructionSteps.STEP_ID_DESCRIPTION, InstructionSteps.STEP_MATTERPORT_ACCOUNT, InstructionSteps.STEP_COPIED_LINK]
  ), [isVirtualVisitFloorfy])

  /** A Floorfy tour is any https link, typed with or without the scheme, as in the admin field; anything else must be a Matterport link. */
  const isValidLink = useCallback((value: string) => (
    isVirtualVisitFloorfy
      ? toVirtualVisitUrl(value, true) !== null
      : matterportPatterns.some((pattern) => pattern.test(value))
  ), [isVirtualVisitFloorfy])

  const [links, setLinks] = useState<LinkItem>()
  const [stepsChecked, setStepsChecked] = useState<Set<InstructionSteps>>(new Set())
  const [comments, setComments] = useState<string>('')

  // QUERY
  const saveVirtualVisitLinks = useSaveVirtualVisitLinks()

  /** Represents the array of links based on the assignment data if it meets the specified conditions. */
  const virtualVisitLinks = useMemo(() => {
    if (!assignmentData) return null
    if (AssignmentDTOIsClientDTO(assignmentData)) return null
    if (!assignmentData.virtualVisitUrls?.length) return null
    return assignmentData.virtualVisitUrls
  }, [assignmentData])

  /** Represents the upload status based on the current assignment data and roles. */
  const status: UploadStatus = useMemo(() => {
    const ctDelivered = !!assignmentData?.stage ? stagesDisplayingDeliveredLinks.has(assignmentData?.stage) : false

    if (roles.isCreative && !!virtualVisitLinks && ctDelivered) return UploadStatus.CT_LINKS_UPLOADED
    if (roles.isAdmin && !!virtualVisitLinks && ctDelivered) return UploadStatus.ADMIN_LINKS_UPLOADED
    if (roles.isCreative || roles.isAdmin && !virtualVisitLinks) return UploadStatus.CT_TO_SUBMIT_LINKS

    return UploadStatus.ADMIN_WAITING_UPLOAD
  }, [assignmentData?.stage, roles.isAdmin, roles.isCreative, virtualVisitLinks])

  /** Adds an empty value for the generated link ID to the existing links object. */
  const onAddLink = useCallback(() => {
    const updatedLinks = { ...links }
    updatedLinks[_generateLinkId()] = {
      link: ''
    }

    setLinks(updatedLinks)
  }, [links])

  /** Removes a link with the specified ID from the existing links object. */
  const onRemoveLink = useCallback((id: string) => {
    if (!!links) {
      const { [id]: _, ...updatedLinks } = links
      setLinks(updatedLinks)
    }
  }, [links])

  /** Callback function for handling link changes and validating the value is a link of the product's tour provider. */
  const onLinkChange = useCallback((e: React.ChangeEvent<HTMLInputElement>, id: string) => {
    const newValue = e.target.value

    setLinks({
      ...links,
      [id]: {
        link: newValue.trim(),
        error: !isValidLink(newValue)
      }
    })
  }, [links, setLinks, isValidLink])

  /** Handles the change event of a checkbox for a specific instruction step. */
  const onChangeCheckbox = useCallback((key: InstructionSteps) => {
    setStepsChecked((prevStepsChecked) => {
      const updatedSteps = new Set(prevStepsChecked)

      if (updatedSteps.has(key)) {
        updatedSteps.delete(key)
      } else {
        updatedSteps.add(key)
      }

      return updatedSteps
    })

  }, [setStepsChecked])

  /** Handles the submission of links. */
  const handleSubmit = useCallback(() => {
    if (!links || Object.values(links).length === 0) return

    // A Floorfy link is stored in its canonical https form, which percent-encodes characters that could break the embed code.
    const arrLinks = Object.values(links).map((item) => (isVirtualVisitFloorfy ? toVirtualVisitUrl(item.link, true) ?? item.link : item.link))

    if (!!assignmentData?.id && !!arrLinks.length) {
      saveVirtualVisitLinks.mutate({
        assignmentId: assignmentData.id,
        payload: {
          virtualVisitUrls: arrLinks,
          creativeCommentForBackbone: comments
        }
      })
    }
  }, [assignmentData?.id, comments, isVirtualVisitFloorfy, links, saveVirtualVisitLinks])

  // INITIAL STATE
  useEffect(() => {
    // Set default links
    if (!!virtualVisitLinks) {
      const defaultLinks = virtualVisitLinks.reduce((items: LinkItem, currentLink: string) => {
        items[`link-${_generateLinkId()}`] = { link: currentLink }
        return items
      }, {})
      setLinks(defaultLinks)
    }
    else {
      setLinks(_generateDefaultLink())
    }
    // Set default comments
    if (assignmentData && (AssignmentDTOIsCreativeDTO(assignmentData) || AssignmentDTOIsAdministratorDTO(assignmentData))) {
      setComments(assignmentData?.creativeCommentsForAdmin ?? '')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // PURGE ALL ON DESTROY
  useEffect(() => {
    return () => {
      saveVirtualVisitLinks.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    links,
    status,
    comments,
    stepsChecked,
    instructionSteps,
    inputErrorKey: isVirtualVisitFloorfy ? 'upload_virtual_visit_instructions.input_error_floorfy' : 'upload_virtual_visit_instructions.input_error',
    virtualVisitLinks,
    saveVirtualVisitLinks,
    setComments,
    handleSubmit,
    onAddLink,
    onLinkChange,
    onRemoveLink,
    onChangeCheckbox,
  }
})
